import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { Upload, X, Loader2 } from "lucide-react";
import { useFeedMediaUpload } from "@/hooks/useFeedMediaUpload";

interface ImageUploadFieldProps {
  label: string;
  /** SAS upload-url endpoint, e.g. "/feed-stickers/upload-url". */
  uploadUrlEndpoint: string;
  /** Currently persisted blob URL (if any). */
  value: string | null | undefined;
  /** Called with the new blob URL once an upload completes. */
  onChange: (blobUrl: string) => void;
  /** Optional id for the file input (label association). */
  id?: string;
  /** Validation error message to display. */
  error?: string;
}

export default function ImageUploadField({
  label,
  uploadUrlEndpoint,
  value,
  onChange,
  id = "image-upload",
  error,
}: ImageUploadFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const { uploadImage, isUploading } = useFeedMediaUpload(uploadUrlEndpoint);

  const handleFile = async (file: File | undefined | null) => {
    if (!file || !file.type.startsWith("image/")) return;
    try {
      const blobUrl = await uploadImage(file);
      onChange(blobUrl);
    } catch {
      // Error toast handled inside the hook.
    }
  };

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <div
        className={cn(
          "border-2 border-dashed rounded-lg p-4 text-center transition-colors",
          isDragging
            ? "border-primary bg-primary/5"
            : "border-muted-foreground/25 hover:border-muted-foreground/50",
          isUploading ? "cursor-wait" : "cursor-pointer",
        )}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragging(false);
          void handleFile(e.dataTransfer.files?.[0]);
        }}
        onClick={() => !isUploading && inputRef.current?.click()}
        data-testid="image-upload-dropzone"
      >
        <input
          ref={inputRef}
          id={id}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => void handleFile(e.target.files?.[0])}
        />
        {isUploading ? (
          <div className="py-6 flex flex-col items-center text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin mb-2" />
            <p className="text-sm">Uploading…</p>
          </div>
        ) : value ? (
          <div className="relative inline-block">
            <img
              src={value}
              alt="Preview"
              className="h-20 mx-auto object-contain"
            />
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="absolute -top-2 -right-2 h-6 w-6"
              onClick={(e) => {
                e.stopPropagation();
                onChange("");
              }}
              aria-label="Remove image"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="py-6">
            <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">
              Drag and drop or click to upload
            </p>
          </div>
        )}
      </div>
      {error && <p className="text-sm font-medium text-destructive">{error}</p>}
    </div>
  );
}
